/**
 * RitualGroupMessageBubble
 * Ritual group message bubble using shared MessageBubble component.
 * Edit + Delete are shown only on the current user's own messages.
 * Organizer moderation (deleting another participant's message in an
 * organizer chat) is enforced server-side only -- the client has no
 * isOrganizer signal on the conversation payload to gate a moderator
 * UI off of, so it does not surface that affordance.
 */

import { useTranslation } from "@/language"
import type { RitualGroupMessage } from "../../types/ritual-group-chat.types"
import { MessageBubble } from "../shared/MessageBubble"

interface RitualGroupMessageBubbleProps {
  message: RitualGroupMessage
  isOwn: boolean
  showSenderInfo?: boolean
  onEdit?: (message: RitualGroupMessage) => void
  onDelete?: (messageId: string) => void
}

export function RitualGroupMessageBubble({
  message,
  isOwn,
  showSenderInfo,
  onEdit,
  onDelete,
}: RitualGroupMessageBubbleProps) {
  const { t } = useTranslation("chats")

  return (
    <MessageBubble
      content={message.content}
      isOwn={isOwn}
      isDeleted={message.isDeleted}
      deletedLabel={t("ritualGroupMessage.deleted")}
      senderId={message.sender?.id}
      senderName={message.sender?.name}
      senderPhoto={message.sender?.profilePhoto}
      showSenderInfo={showSenderInfo}
      onEdit={onEdit ? () => onEdit(message) : undefined}
      onDelete={onDelete ? () => onDelete(message.id) : undefined}
      editLabel={t("ritualGroupMessage.edit")}
      deleteLabel={t("ritualGroupMessage.delete")}
      confirmDeleteLabel={t("ritualGroupMessage.confirmDelete")}
    />
  )
}
